import React, { useState, useEffect } from 'react';
import { Scale, TrendingDown, Users, LogOut, Plus, Loader } from 'lucide-react';

// Change this to your Azure Function URL after deployment
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:7071/api';

export default function WeightTrackerApp() {
  const [user, setUser] = useState(null);
  const [entries, setEntries] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [currentWeight, setCurrentWeight] = useState('');
  const [view, setView] = useState('home');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    if (user) {
      loadEntries(user.id);
      loadLeaderboard();
    }
  }, [user]);

  const loadUserData = async () => {
    try {
      // First check localStorage for quick load
      const userData = localStorage.getItem('user');
      let parsed = null;
      
      if (userData) {
        parsed = JSON.parse(userData);
        setUser(parsed);
      }
      
      // Then check Azure authentication
      const authResponse = await fetch('/.auth/me');
      const authData = await authResponse.json();
      
      if (authData.clientPrincipal) {
        const authUser = authData.clientPrincipal;
        const userId = authUser.userDetails.replace(/[^a-zA-Z0-9]/g, '_');
        
        // Fetch latest user data from API
        const response = await fetch(`${API_URL}/users/${userId}`);
        if (response.ok) {
          const apiUser = await response.json();
          setUser(apiUser);
          localStorage.setItem('user', JSON.stringify(apiUser));
          await loadEntries(apiUser.id);
        } else {
          // Create new user if doesn't exist
          const newUser = {
            id: userId,
            userId: userId,
            email: authUser.userDetails,
            name: authUser.userDetails.split('@')[0],
            startingWeight: null,
            currentWeight: null,
            percentLost: 0
          };
          
          const createResponse = await fetch(`${API_URL}/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newUser)
          });
          
          if (createResponse.ok) {
            const savedUser = await createResponse.json();
            setUser(savedUser);
            localStorage.setItem('user', JSON.stringify(savedUser));
          } else if (parsed) {
            // Fallback to localStorage if API fails
            setUser(parsed);
            await loadEntries(parsed.id);
          }
        }
        
        await loadLeaderboard();
      } else if (parsed) {
        // No auth but have localStorage - use it
        await loadEntries(parsed.id);
        await loadLeaderboard();
      }
    } catch (e) {
      console.log('Error loading user data:', e);
      // Try localStorage as fallback
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const parsed = JSON.parse(userData);
          setUser(parsed);
          await loadEntries(parsed.id);
          await loadLeaderboard();
        }
      } catch (err) {
        console.log('Fallback also failed:', err);
      }
    }
    setLoading(false);
  };

  const loadEntries = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/entries/${userId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded entries:', data);
        setEntries(Array.isArray(data) ? data : []);
      } else {
        console.log('Failed to load entries:', response.status);
        setEntries([]);
      }
    } catch (e) {
      console.log('Error loading entries:', e);
      setEntries([]);
    }
  };

  const loadLeaderboard = async () => {
    try {
      const response = await fetch(`${API_URL}/leaderboard`);
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data);
      }
    } catch (e) {
      console.log('Error loading leaderboard:', e);
    }
  };

  const handleLogin = (provider) => {
    window.location.href = `/.auth/login/${provider}`;
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/.auth/me');
      const data = await response.json();
      
      if (data.clientPrincipal) {
        const authUser = data.clientPrincipal;
        const userId = authUser.userDetails.replace(/[^a-zA-Z0-9]/g, '_');
        
        // Check if user exists in database
        const userResponse = await fetch(`${API_URL}/users/${userId}`);
        if (userResponse.ok) {
          const existingUser = await userResponse.json();
          setUser(existingUser);
          localStorage.setItem('user', JSON.stringify(existingUser));
          await loadEntries(existingUser.id);
        } else {
          // Create new user
          const newUser = {
            id: userId,
            userId: userId,
            email: authUser.userDetails,
            name: authUser.userDetails.split('@')[0],
            startingWeight: null,
            currentWeight: null,
            percentLost: 0
          };
          
          const createResponse = await fetch(`${API_URL}/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newUser)
          });
          
          if (createResponse.ok) {
            const savedUser = await createResponse.json();
            setUser(savedUser);
            localStorage.setItem('user', JSON.stringify(savedUser));
          }
        }
        await loadLeaderboard();
      }
    } catch (e) {
      console.log('No authenticated user:', e);
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    window.location.href = '/.auth/logout';
  };

  const addWeightEntry = async () => {
    if (!currentWeight || parseFloat(currentWeight) <= 0) {
      alert('Please enter a valid weight');
      return;
    }

    setSaving(true);

    const weight = parseFloat(currentWeight);
    
    // Use existing starting weight if available, otherwise use current weight as starting weight
    const starting = user.startingWeight || weight;

    const newEntry = {
      id: `${user.id}_${Date.now()}`,
      userId: user.id,
      weight,
      date: new Date().toISOString(),
      percentLost: ((starting - weight) / starting * 100).toFixed(1)
    };

    const percentLost = parseFloat(newEntry.percentLost);

    const updatedUser = {
      ...user,
      startingWeight: starting,
      currentWeight: weight,
      percentLost
    };

    try {
      // Save entry
      const entryResponse = await fetch(`${API_URL}/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newEntry)
      });

      if (!entryResponse.ok) {
        throw new Error('Failed to save entry');
      }

      // Update user
      const userResponse = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedUser)
      });

      if (!userResponse.ok) {
        throw new Error('Failed to update user');
      }

      const savedUser = await userResponse.json();
      
      setUser(savedUser);
      localStorage.setItem('user', JSON.stringify(savedUser));
      setCurrentWeight('');
      
      await loadEntries(user.id);
      await loadLeaderboard();
      
      alert('Weight entry added successfully!');
    } catch (e) {
      alert('Error adding entry: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <div className="text-xl text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-block mb-4">
              <img 
                src="/logo.png" 
                alt="Fat Camp Logo" 
                className="w-48 h-48 object-contain mx-auto"
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Fat Camp</h1>
            <p className="text-gray-600">Weigh In and Cry</p>
            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-800 font-semibold">✨ Hosted on Azure Free Tier</p>
              <p className="text-xs text-green-700 mt-1">Static Web Apps + Cosmos DB + Functions</p>
            </div>
          </div>
          
          <button
            onClick={() => handleLogin('google')}
            className="w-full bg-white border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-3 mb-3"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>
          
          <button
            onClick={() => handleLogin('aad')}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-3"
          >
            <svg className="w-5 h-5" viewBox="0 0 23 23" fill="currentColor">
              <path d="M11.03 0L0 1.95v9.29c0 7.35 4.97 14.2 11.03 15.76 6.05-1.56 11.03-8.41 11.03-15.76V1.95L11.03 0z"/>
            </svg>
            Sign in with Microsoft
          </button>
          
          <div className="mt-6 text-center text-sm text-gray-600">
            <a href="/privacy.html" className="hover:underline">Privacy Policy</a>
            {' · '}
            <a href="/terms.html" className="hover:underline">Terms of Service</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img 
              src="/logo.png" 
              alt="Fat Camp" 
              className="w-8 h-8 object-contain"
            />
            <span className="font-bold text-gray-800">Fat Camp</span>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Azure Free</span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 pb-20">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Welcome, {user.name}!</h2>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">Starting Weight</p>
              <p className="text-2xl font-bold text-blue-600">
                {user.startingWeight ? `${user.startingWeight} lbs` : '--'}
              </p>
            </div>
            <div className={`rounded-lg p-4 ${user.percentLost > 0 ? 'bg-green-50' : user.percentLost < 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
              <p className="text-sm text-gray-600">Weight Lost</p>
              <p className={`text-2xl font-bold ${user.percentLost > 0 ? 'text-green-600' : user.percentLost < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                {user.percentLost !== undefined ? `${user.percentLost}%` : '0%'}
              </p>
            </div>
          </div>
        </div>

        <div className="text-center mb-4">
          <a href="/privacy" className="text-sm text-blue-600 hover:underline">Privacy Policy</a>
        </div>

        <nav className="flex gap-2 mb-6">
          <button
            onClick={() => setView('home')}
            className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${
              view === 'home' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'
            }`}
          >
            <TrendingDown className="w-5 h-5 inline mr-2" />
            My Progress
          </button>
          <button
            onClick={() => setView('leaderboard')}
            className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${
              view === 'leaderboard' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'
            }`}
          >
            <Users className="w-5 h-5 inline mr-2" />
            Leaderboard
          </button>
        </nav>

        {view === 'home' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Add Weight Entry</h3>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.1"
                  value={currentWeight}
                  onChange={(e) => setCurrentWeight(e.target.value)}
                  placeholder="Enter weight (lbs)"
                  disabled={saving}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                />
                <button
                  onClick={addWeightEntry}
                  disabled={saving}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:bg-gray-400"
                >
                  {saving ? <Loader className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                  {saving ? 'Saving...' : 'Add'}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Weight History</h3>
              {entries.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No entries yet. Add your first weight!</p>
              ) : (
                <div className="space-y-3">
                  {entries.map((entry) => (
                    <div key={entry.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-800">{entry.weight} lbs</p>
                        <p className="text-sm text-gray-600">
                          {new Date(entry.date).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${parseFloat(entry.percentLost) > 0 ? 'text-green-600' : parseFloat(entry.percentLost) < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                          {parseFloat(entry.percentLost) > 0 ? '' : parseFloat(entry.percentLost) < 0 ? '+' : ''}{Math.abs(parseFloat(entry.percentLost))}%
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {view === 'leaderboard' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Leaderboard</h3>
            {leaderboard.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No entries yet. Be the first!</p>
            ) : (
              <div className="space-y-3">
                {leaderboard.map((u, index) => (
                  <div key={u.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-700' : 'bg-blue-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{u.name}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${u.percentLost > 0 ? 'text-green-600' : u.percentLost < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                        {u.percentLost}%
                      </p>
                      <p className="text-xs text-gray-500">weight {u.percentLost > 0 ? 'lost' : u.percentLost < 0 ? 'gained' : 'unchanged'}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}